/**
 * Report Export Utilities
 *
 * Functions for exporting report data to CSV and printing to PDF.
 */

import { ReportDefinition, ReportContext } from '../types/reports';

/**
 * Escape a value for CSV format
 */
function escapeCSV(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }
  const stringValue = String(value);
  // Escape if contains comma, quote, or newline
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

/**
 * Format a date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Export report data to CSV file
 */
export function exportReportToCSV(
  report: ReportDefinition,
  context: ReportContext,
  data: Record<string, unknown>[],
  columns: { key: string; header: string }[]
): void {
  const rows: string[] = [];

  // Header section
  rows.push(`Report: ${escapeCSV(report.title)}`);
  rows.push(`Location: ${escapeCSV(context.locationName)}`);
  rows.push(`Department: ${escapeCSV(context.department.replace('_', ' '))}`);
  rows.push(`Date Range: ${formatDate(context.startDate)} - ${formatDate(context.endDate)}`);
  rows.push(`Generated: ${formatDate(new Date().toISOString())}`);
  rows.push(''); // Empty row

  // Column headers
  rows.push(columns.map((col) => escapeCSV(col.header)).join(','));

  // Data rows
  data.forEach((row) => {
    const values = columns.map((col) => escapeCSV(row[col.key] as string | number));
    rows.push(values.join(','));
  });

  // Create and download file
  const csvContent = rows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  // Generate filename
  const dateStr = new Date().toISOString().split('T')[0];
  const reportName = report.id.replace(/[^a-z0-9]/gi, '-');
  const filename = `${reportName}_${context.locationId}_${dateStr}.csv`;

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Print report to PDF using browser print dialog
 * This triggers the print dialog with print-optimized styles
 */
export function printReportToPDF(
  report: ReportDefinition,
  context: ReportContext,
  printElementId: string
): void {
  const printElement = document.getElementById(printElementId);
  if (!printElement) {
    console.error(`Print element with id "${printElementId}" not found`);
    return;
  }

  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups to print this report.');
    return;
  }

  // Build print HTML
  const printHTML = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${report.title} - ${context.locationName}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #1f2937;
          padding: 20px;
        }
        .report-header {
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e5e7eb;
        }
        .report-title {
          font-size: 18px;
          font-weight: bold;
          margin: 0 0 8px 0;
        }
        .report-meta {
          font-size: 11px;
          color: #6b7280;
        }
        .report-meta span {
          margin-right: 16px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 16px;
        }
        th, td {
          padding: 8px;
          text-align: left;
          border-bottom: 1px solid #e5e7eb;
        }
        th {
          background-color: #f9fafb;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        tr:nth-child(even) {
          background-color: #f9fafb;
        }
        .text-right {
          text-align: right;
        }
        .currency {
          font-family: 'SF Mono', Monaco, monospace;
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="report-header">
        <h1 class="report-title">${report.title}</h1>
        <div class="report-meta">
          <span><strong>Location:</strong> ${context.locationName}</span>
          <span><strong>Department:</strong> ${context.department.replace('_', ' ')}</span>
          <span><strong>Period:</strong> ${formatDate(context.startDate)} - ${formatDate(context.endDate)}</span>
          <span><strong>Generated:</strong> ${formatDate(new Date().toISOString())}</span>
        </div>
      </div>
      ${printElement.innerHTML}
    </body>
    </html>
  `;

  printWindow.document.write(printHTML);
  printWindow.document.close();

  // Wait for content to load, then print
  printWindow.onload = () => {
    printWindow.print();
    printWindow.close();
  };
}

/**
 * Simple print using window.print() for the current page
 * Used when we want to print the modal content directly
 */
export function printCurrentPage(): void {
  window.print();
}
